import React, { useState, useEffect, useRef } from "react";
import { Container, Typography, Box, Button } from "@mui/material";
import ProjectCard from "../components/ProjectCard";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const projects = {
	web: [
		{
			title: "Apache Superset",
			image: require("../projectImages/superset.jpg"),
			description:
				"Contributed as an open source developer by proposing, designing, and implementing improvements through a submitted contribution proposal",
			technologies: ["Typescript", "Jest"]
		},
		{
			title: "CASPR",
			image: require("../projectImages/caspr.png"),
			description:
				"Interactive 3D visualization web application that allows users to explore complex causal relationships by uploading personalized JSON data",
			link: "https://caspr.vercel.app/",
			technologies: ["Next.js", "D3.js", "Firebase", "Typescript"]
		},
		{
			title: "Recipe Book",
			image: require("../projectImages/studentrecipe.png"),
			description:
				"A MERN application for users to explore and review cooking recipes, and system to manage users and view statistics",
			link: "https://student-recipe-morris.vercel.app/",
			technologies: ["MongoDB", "Express", "React", "Node.js"]
		},
		{
			title: "(Fun) Personal website",
			image: require("../projectImages/website.png"),
			description:
				"An interactive old personal website that allows users to discover hidden items on screen",
			link: "https://morilili.github.io/",
			technologies: ["HTML", "CSS", "React"]
		}
	],
	mobile: [
		{
			title: "Course Manager and Planner",
			image: require("../projectImages/b07.png"),
			description:
				"Android mobile app for students to manage courses and plan future semesters using a timeline generating algorithm, and admins to perform CRUD operations for courses",
			link: "",
			technologies: ["Java", "Android SDK"]
		}
	],
	game_dev: [
		{
			title: "Minesweeper",
			image: require("../projectImages/minesweeper.jpg"),
			description:
				"Recreated a Minesweeper game using Python and implemented the recursive backtracking algorithm",
			link: "https://github.com/Morilili/Minesweeper",
			technologies: ["Python", "pyFLTK"]
		},
		{
			title: "Sokoban",
			image: require("../projectImages/sokoban.png"),
			description:
				"A classic puzzle solving game written completely in Assembly under the RISC-V 32-bit architecture",
			link: "",
			technologies: ["Assembly", "RISC-V"]
		}
	],
	misc: [
		{
			title: "Personal Cloud Server",
			image: require("../projectImages/94b1d1e3-nextcloud.jpg"),
			description:
				"Configured and deployed a private cloud server using NextCloud. Implemented secure remote access and optimized for minimal resource consumption",
			link: "https://cloud.morrisho.me/",
			technologies: ["NextCloud", "Apache", "MySQL"]
		},
		{
			title: "TicketUp",
			image: require("../projectImages/ticketup.png"),
			description:
				"A startup that provides an HR software solution to streamline and optimize communication between HR and employees",
			link: "",
			technologies: ["Figma", "React"]
		},
		{
			title: "MADFUT 25 Game Automator",
			image: require("../projectImages/madfut.png"),
			description:
				"Automate repetitive game modes with python scripts by using ADB for on screen actions and Pytesseract to recognise game outputs",
			link: "",
			technologies: ["Python", "ADB", "Pytesseract"]
		}
	]
};

const projectCategories = ["web", "mobile", "game_dev", "misc"];

const Projects = () => {
	const [value, setValue] = useState(0);
	const [justifyContent, setJustifyContent] = useState("center");
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(true);
	const [, setFade] = useState(true);
	const containerRef = useRef(null);

	const handleButtonClick = (newValue) => {
		setFade(false);
		setTimeout(() => {
			setValue(newValue);
			setFade(true);
		}, 300);
	};

	useEffect(() => {
		const category = projectCategories[value];
		if (projects[category]) {
			const containerWidth = containerRef.current
				? containerRef.current.offsetWidth
				: 0;
			const totalCardWidth = projects[category].length * 420;
			setJustifyContent(
				totalCardWidth > containerWidth ? "flex-start" : "center"
			);
		}
	}, [value]);

	const handleScroll = () => {
		const container = containerRef.current;
		if (container) {
			setCanScrollLeft(container.scrollLeft > 0);
			setCanScrollRight(
				container.scrollLeft < container.scrollWidth - container.clientWidth - 1
			);
		}
	};

	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			container.addEventListener("scroll", handleScroll);
			handleScroll();
			return () => container.removeEventListener("scroll", handleScroll);
		}
	}, [value]);

	return (
		<Container disableGutters>
			<Typography
				variant="h5"
				align="left"
				sx={{ fontWeight: "bold" }}
				gutterBottom
			>
				Projects
			</Typography>
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					gap: 2,
					marginBottom: 2
				}}
			>
				<Button
					variant={value === 0 ? "contained" : "outlined"}
					onClick={() => handleButtonClick(0)}
					sx={{
						borderRadius: "20px",
						backgroundColor: value === 0 ? "#383838" : "#202125",
						color: value === 0 ? "#ffffff" : "#d4d4d4",
						borderColor: "#383838",
						"&:hover": {
							backgroundColor: value === 0 ? "#4a4a4a" : "#383838"
						}
					}}
				>
					Web
				</Button>
				<Button
					variant={value === 1 ? "contained" : "outlined"}
					onClick={() => handleButtonClick(1)}
					sx={{
						borderRadius: "20px",
						backgroundColor: value === 1 ? "#383838" : "#202125",
						color: value === 1 ? "#ffffff" : "#d4d4d4",
						borderColor: "#383838",
						"&:hover": {
							backgroundColor: value === 1 ? "#4a4a4a" : "#383838"
						}
					}}
				>
					Mobile
				</Button>
				<Button
					variant={value === 2 ? "contained" : "outlined"}
					onClick={() => handleButtonClick(2)}
					sx={{
						borderRadius: "20px",
						backgroundColor: value === 2 ? "#383838" : "#202125",
						color: value === 2 ? "#ffffff" : "#d4d4d4",
						borderColor: "#383838",
						"&:hover": {
							backgroundColor: value === 2 ? "#4a4a4a" : "#383838"
						}
					}}
				>
					Game Dev
				</Button>
				<Button
					variant={value === 3 ? "contained" : "outlined"}
					onClick={() => handleButtonClick(3)}
					sx={{
						borderRadius: "20px",
						backgroundColor: value === 3 ? "#383838" : "#202125",
						color: value === 3 ? "#ffffff" : "#d4d4d4",
						borderColor: "#383838",
						"&:hover": {
							backgroundColor: value === 3 ? "#4a4a4a" : "#383838"
						}
					}}
				>
					Misc
				</Button>
			</Box>
			<Box
				ref={containerRef}
				sx={{
					display: "flex",
					flexWrap: "nowrap",
					overflowX: "auto",
					justifyContent: justifyContent,
					width: "100%",
					"&::-webkit-scrollbar": { display: "none" },
					"-ms-overflow-style": "none",
					"scrollbar-width": "none",
					transition: "all 0.5s ease-in-out"
				}}
			>
				{projects[projectCategories[value]]?.map((project, index) => (
					<Box key={index} sx={{ minWidth: "420px", margin: "0 10px" }}>
						<ProjectCard {...project} />
					</Box>
				))}
			</Box>
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
				{canScrollLeft && (
					<Box
						sx={{
							cursor: "pointer",
							padding: "5px",
							opacity: 0.7,
							transition: "opacity 0.3s",
							"&:hover": { opacity: 1 }
						}}
						onClick={() => {
							containerRef.current.scrollBy({ left: -300, behavior: "smooth" });
						}}
					>
						<ArrowBackIosIcon />
					</Box>
				)}
				{canScrollRight && (
					<Box
						sx={{
							cursor: "pointer",
							padding: "5px",
							opacity: 0.7,
							transition: "opacity 0.3s",
							"&:hover": { opacity: 1 }
						}}
						onClick={() => {
							containerRef.current.scrollBy({ left: 300, behavior: "smooth" });
						}}
					>
						<ArrowForwardIosIcon />
					</Box>
				)}
			</Box>
		</Container>
	);
};

export default Projects;
